import { useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { entrarComSenha } from '../../auth/loginLocal'
import { Botao } from '../../components/Botao'
import { CampoDeTexto } from '../../components/campos'
import { Icone, IconeAlerta, type PropsDeIcone } from '../../components/Icones'
import { cn } from '../../lib/cn'
import { TEXTOS } from '../../lib/glossario'

/**
 * O que a tela cobra antes de ir ao servidor: que os dois campos existam.
 *
 * Nada além disso. Formato de usuário, domínio e tamanho de senha são regra do servidor e
 * mudam lá — repeti-los aqui faria a tela recusar credencial boa em silêncio no dia em que a
 * regra mudasse, sem nenhum log para explicar.
 */
const esquema = z.object({
  usuario: z.string().trim().min(1, TEXTOS.login.faltaPreencher),
  senha: z.string().min(1, TEXTOS.login.faltaPreencher),
})

type Credenciais = z.infer<typeof esquema>

interface Props {
  /** Chamado quando uma tentativa começa, para a tela limpar avisos de tentativas anteriores. */
  aoTentar: () => void
  /** Chamado depois que a sessão foi gravada. */
  aoEntrar: () => void
}

/**
 * Usuário e senha.
 *
 * A senha não é aparada nem tratada: espaço no começo ou no fim pode ser parte dela, e um
 * `trim()` de conveniência transformaria senha certa em "usuário ou senha incorretos" para
 * quem usa gerenciador de senhas. No usuário o `trim()` é seguro e resolve o espaço que vem
 * junto quando se cola o e-mail.
 *
 * @param props o que fazer ao começar a tentativa e ao entrar
 * @returns o formulário
 */
export function FormularioDeEntrada({ aoTentar, aoEntrar }: Props) {
  const [senhaVisivel, setSenhaVisivel] = useState(false)
  const [aviso, setAviso] = useState<string | null>(null)
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Credenciais>({ resolver: zodResolver(esquema) })

  const enviar = handleSubmit(async ({ usuario, senha }) => {
    setAviso(null)
    aoTentar()
    const problema = await entrarComSenha(usuario, senha)
    if (problema) {
      setAviso(problema)
      return
    }
    aoEntrar()
  })

  return (
    <form onSubmit={enviar} className="space-y-4" noValidate>
      {aviso && (
        <p role="alert" className="flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm font-medium text-red-900">
          <IconeAlerta className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
          <span>{aviso}</span>
        </p>
      )}

      <CampoDeTexto
        rotulo={TEXTOS.login.usuario}
        erro={errors.usuario?.message}
        autoComplete="username"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        {...register('usuario')}
      />

      <div>
        <CampoDeTexto
          rotulo={TEXTOS.login.senha}
          erro={errors.senha?.message}
          type={senhaVisivel ? 'text' : 'password'}
          autoComplete="current-password"
          {...register('senha')}
        />
        <div className="mt-1.5 flex justify-end">
          <BotaoDeVisibilidade visivel={senhaVisivel} aoAlternar={() => setSenhaVisivel((atual) => !atual)} />
        </div>
      </div>

      <Botao type="submit" aparencia="primario" className="w-full" disabled={isSubmitting}>
        {isSubmitting ? TEXTOS.login.entrando : TEXTOS.login.botao}
      </Botao>
    </form>
  )
}

/**
 * Mostrar ou ocultar a senha.
 *
 * O rótulo muda com o estado e não há `aria-pressed`: com os dois, o leitor de tela anuncia
 * "Mostrar senha, não pressionado", e a pessoa tem de decidir se "não pressionado" desfaz o
 * "mostrar". Um texto que diz o que o clique vai fazer basta e não é ambíguo.
 *
 * Fica abaixo do campo, e não sobreposto dentro dele, porque a posição de um botão flutuante
 * dependeria da altura exata do rótulo e do erro desenhados por `CampoDeTexto` — dois valores
 * de outro arquivo, que ninguém lembraria de conferir ao mexer lá.
 *
 * @param props se a senha está à mostra e o que fazer ao alternar
 * @returns o botão
 */
function BotaoDeVisibilidade({ visivel, aoAlternar }: { visivel: boolean; aoAlternar: () => void }) {
  return (
    <button
      type="button"
      onClick={aoAlternar}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md px-1.5 py-1 text-xs font-medium',
        'text-niteroi-700 transition-colors hover:bg-niteroi-50 hover:text-niteroi-900',
      )}
    >
      <IconeOlho aberto={visivel} className="h-4 w-4" />
      {visivel ? 'Ocultar senha' : 'Mostrar senha'}
    </button>
  )
}

/** O olho do botão acima, riscado quando a senha está escondida. Decoração: o texto já nomeia. */
function IconeOlho({ aberto, ...props }: PropsDeIcone & { aberto: boolean }) {
  return (
    <Icone {...props}>
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
      <circle cx="12" cy="12" r="3" />
      {!aberto && <path d="M4 20 20 4" />}
    </Icone>
  )
}
